<template>
    <div class="mb-2">
        <div v-if="isArchived" class="p-4 mt-4 mb-4 text-sm text-white bg-red-500 rounded-md">
            <strong>Note:</strong> This task has been archived.
        </div>
        <Button label="Cancel" icon="pi pi-arrow-left" @click="goBack" class="z-1 mr-2" severity="warning" />
        <Button :label="`Go to ${label('Task')}`" icon="pi pi-align-left" @click="gotoTasks" class="z-1" />
    </div>
    <div class="grid">
        <div class="container col-12 md:col-7">
            <div v-if="loading" class="card">
                <task-details-loader />
            </div>
            <div v-else class="card">
                <div class="flex flex-column mb-3">
                    <!-- <div class=""> -->
                    <!-- </div> -->
                    <div class="flex justify-content-between">
                        <h6>{{ label('Task') }} Details and Description:</h6>
                    </div>
                </div>
                <Accordion :multiple="true" :activeIndex="[0, 1, 2, 3, 4]">
                    <AccordionTab :header="`${label('Task')} Details`">
                        <div>

                            <div class="formgrid grid">
                                <div class="field col-2">
                                    Author name:
                                </div>
                                <div class="field col-4">
                                    <b>{{ task.author }}</b>
                                </div>
                                <div class="field col-2">
                                    Created at:
                                </div>
                                <div class="field col-4">
                                    <b>{{ task.created }}</b>
                                </div>
                                <div class="field col-2">
                                    Author email:
                                </div>
                                <div class="field col-4">
                                    <b>{{ task.authorEmail }}</b>
                                </div>

                                <div class="col-12">
                                    <hr />
                                </div>
                            </div>

                            <!-- From Department Section -->
                            <span class="font-bold">From:</span>
                            <br />
                            <base-bread-crumb :items="task.from_dept_breadcrumb" aria-label="breadcrumb" />
                            <custom-chip :label="task.department_from" entity="Department" />

                            <!-- To Department Section -->
                            <span class="font-bold">To:</span><br />
                            <base-bread-crumb :items="task.to_dept_breadcrumb" aria-label="breadcrumb" />
                            <custom-chip :label="task.department_to" entity="Department" />

                            <!-- Source and Theme Section -->
                            <div class="formgrid grid">
                                <div class="field col-6">
                                    <base-bread-crumb :items="task.src_breadcrumb" aria-label="breadcrumb" />
                                    <custom-chip :label="task.source" entity="Source" />
                                </div>
                                <div class="field col-6">
                                    <base-bread-crumb :items="task.theme_breadcrumb" aria-label="breadcrumb" />
                                    <custom-chip :label="task.theme" entity="Theme" />
                                </div>
                            </div>
                            <!-- Title Section-->
                            <div class="formgrid grid">
                                <div class="field col-12">
                                    <custom-chip :label="task.title" :entity="`${label('Task')} or Action Title`" />
                                </div>
                            </div>
                            <!-- Assigned and Notified Users Section -->
                            <div class="formgrid grid">
                                <div class="field col-6">
                                    <label for="assignedTo" class="font-bold">Assigned Users:</label>
                                    <div v-if="task.users_assigned_to?.length">
                                        <custom-chip v-for="user in task.users_assigned_to" :key="user.id" :label="user.fullname" :blueChip="true" />
                                    </div>
                                    <div v-else>
                                        <span>Users not assigned</span>
                                    </div>
                                </div>
                                <div class="field col-6">
                                    <label for="notifiedTo" class="font-bold">Notified Users:</label>
                                    <div v-if="task.users_notified?.length">
                                        <custom-chip v-for="user in task.users_notified" :key="user.id" :label="user.fullname" :blueChip="true" />
                                    </div>
                                    <div v-else>
                                        <span>No users notified</span>
                                    </div>
                                </div>
                            </div>
                            <div class="formgrid grid">
                                <div class="field col">
                                    <label for="startDate" class="block font-bold">Start Date:</label>
                                    <span v-if="task.start_date">{{ task.start_date }}</span>
                                </div>
                                <div class="field col">
                                    <label for="dueDate" class="block font-bold">Due Date:</label>
                                    <span v-if="task.due_date">{{ task.due_date }}</span>
                                </div>
                                <div class="field col">
                                    <custom-tag label="Priority Set" entity="priority" :id="task.priority" />
                                </div>
                                <div  v-if="!isArchived" class="field col">
                                    <custom-tag label="Progress Status" entity="progress" :id="task.progress" />
                                </div>
                                <div v-else  class="field col">
                                    <custom-tag label="Progress Status" entity="archive" :id="'archive'" />
                                </div>
                            </div>
                        </div>
                    </AccordionTab>
                    <AccordionTab :header="`${label('Task')} or Action Description`">
                        <div v-if="task.description" v-html="task.description" />
                        <div v-else>No task description...</div>
                    </AccordionTab>
                    <AccordionTab header="All Attachments">
                        <file-attachments :attachments="task.attachment" />
                    </AccordionTab>
                    <AccordionTab header="Images Preview">
                        <gallery-image-preview :attachments="task.attachment" />
                    </AccordionTab>
                    <AccordionTab :header="`${label('Task')} linked to`">
                        <linked-task-list :linkedTasks="task.linked_to" />
                    </AccordionTab>
                    <AccordionTab :header="`${label('Task')} linked by`">
                        <linked-task-list :linkedTasks="task.linked_by" />
                    </AccordionTab>
                </Accordion>
            </div>
        </div>

        <div class="container col-12 md:col-5">
            <div class="card">
                <!-- Task Status Change Section -->
                <task-status-details :task="task" :switchableStatus="taskStatusSwitchable" @task-status-changed="switchStatus" />
                <!--<task-assignee-details :canDelegateTask="canDelegateCurrentTask" :task="task" />-->

                <TaskAssignee :canDelegateTask="canDelegateCurrentTask" :task="task" @taskDelegation="taskDelegationHandler"/>

                <h6 class="tc-section-title"><i class="pi pi-comments"></i> Comment / Updates Box</h6>
                <Accordion :multiple="true" :activeIndex="[0, 1]" class="tc-accordion">
                    <AccordionTab :header="`${label('Task')} Comments`">
                        <task-comments :taskId="taskId" />
                    </AccordionTab>
                    <AccordionTab header="Add a Comment" v-if="!isArchived" >
                        <add-task-comment :taskId="taskId"/>
                    </AccordionTab>
                </Accordion>
            </div>
        </div>

        <div class=" col-12">
            <div class="card">
                <Accordion :activeIndex="1">
                    <AccordionTab header="Events">
                        <LogViewer :event_type="logEntityTypes" :entity_ids="entityLogIds"/>
                    </AccordionTab>
                </Accordion>
            </div>
        </div>
    </div>
</template>

<script setup>
import TaskDetailsLoader from '../../skeleton/TaskDetailsLoader.vue';
import TaskStatusDetails from '../components/TaskStatusDetails.vue';
//import TaskAssigneeDetails from '../components/TaskAssigneeDetails.vue';
import FileAttachments from '../../../../../modules/file/components/FileAttachments.vue';
import LinkedTaskList from '../components/LinkedTaskList.vue';
import TaskComments from '../components/TaskComments.vue';
import AddTaskComment from '../components/AddTaskComment.vue';
import GalleryImagePreview from '../../../../../components/GalleryImagePreview.vue';

import { ref, computed, defineProps, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { executeLoader } from '../../../utils';
import { formatDate, formatDisplayDate } from '../../../../../utilities/date-formatter';
import { formatChildParentBreadCrumb } from '../../../../../utilities/json-formatter';
import {
    authStore,
    taskStore,
    departmentStore,
    sourceStore,
    themeStore,
    commentStore,
} from '../../../../../store/global-stores';
import LogViewer from '@/product/ams/components/LogViewer.vue';
import TaskAssignee from '@/product/ams/modules/task/components/TaskAssignee.vue';
import { authApi } from '@/utilities/axios';
import EventBus from '@/utilities/event-bus';
import { useLabels } from '@/composables/LabelComposable';
// Mute 403-triggered toasts (interceptor + cascading store "Failed to fetch" toasts)
// for the wrapped async call only.
const withSuppressed403Toasts = async (fn) => {
    const originalEmit = EventBus.emit;
    let last403At = 0;
    EventBus.emit = function (event, payload) {
        console.log('event', event, 'payload', payload);
        if (event === 'show-toast') {
            const detail = payload?.detail ?? '';
            if (detail === 'Not enough permissions to access') {
                last403At = Date.now();
                return;
            }
            if (last403At && Date.now() - last403At < 150) {
                return;
            }
        }
        return originalEmit.apply(this, arguments);
    };
    try {
        return await fn();
    } finally {
        EventBus.emit = originalEmit;
    }
};

const { label } = useLabels();
const { comments } = storeToRefs(commentStore);
const { isArchived } = storeToRefs(taskStore);

const entityLogIds = computed(() => {
    let arrayList = []
    arrayList.push(taskId.value)

    if (task.value.attachment) {
        task.value.attachment.forEach(mapper => {
            arrayList.push(mapper.file.id)
        })
    }

    if (comments.value) {
        comments.value.forEach(item => {
            arrayList.push(item.id)
            if (item.attachment) {
                item.attachment.forEach(attachment => {
                    if (attachment.file) {
                        arrayList.push(attachment.file.id)
                    }
                })
            }
        })
    }

    return arrayList
})

const logEntityTypes = [
    'file',
    'comment',
    'task',
]

const { loggedUser, userIsGlobalTaskForce } = storeToRefs(authStore);
// props
const props = defineProps({
    id: {
        type: String,
        required: true,
    },
});

const router = useRouter();

// states
const loading = ref(false);
const task = ref({
    id: null,
    author: null,
    authorEmail: null,
    department_from: null,
    from_dept_breadcrumb: null,
    department_to: null,
    to_dept_breadcrumb: null,
    source: null,
    src_breadcrumb: null,
    theme: null,
    theme_breadcrumb: null,
    title: null,
    users_assigned_to: [],
    users_notified: [],
    linked_to: [],
    linked_by: [],
    start_date: null,
    due_date: null,
    priority: null,
    progress: null,
    // reminder_date: null,
    description: null,
    attachment: [],
    created: null,
});

const managers = ref([]); //used for task status changes permission validity check

// computed properties
const taskId = computed(() => {
    return parseInt(props.id);
});

// returns true is the task progress is completed
const taskCompleted = computed(() => {
    return task.value?.progress === 3;
});

// Checks whether the logged user is task assignee or manager of assignee's departments
const userAllowed = computed(() => {
    // extract the list of ids of users whom the task has been assigned to
    let allowedUsers = task.value?.users_assigned_to?.map((user) => user.id);
    // append existing managers into allowedUsers since managers are also able to make changes
    allowedUsers = managers.value?.length > 0 ? allowedUsers?.concat(managers.value) : allowedUsers;
    //check for the id of the current logged user
    const loggedUserId = loggedUser.value?.id;
    // check if the loggedUserId exists in the allowed Users list
    // if either of the condition matches, then disable the progress change
    return allowedUsers?.includes(loggedUserId) ?? false;
});

// checks if task status in not switchable
const taskStatusSwitchable = computed(() => {
    return task.value.statusSettable

    // Check the following if logged user is
    //return userAllowed.value
});

// check if task can be delegated by logged user
const canDelegateCurrentTask = computed(() => {
    // Check the following if logged user is:-
    // a task assignee or manager of department of task assignee, author
    return !taskCompleted.value ? userAllowed.value || userIsGlobalTaskForce.value : false;
});

onMounted(async () => {
    executeLoader(true);
    loading.value = true;
    // debugger;
    await withSuppressed403Toasts(loadTask);
    loading.value = false;
    executeLoader(false);
});

onUnmounted(() => {
    initTaskDetails();
});

const loAttachments = ref([])

const loAuthor = ref(null);

const fetchLoAttachments = async (id) => {
  try {
    const response = await authApi.get(`/v2/learning-outcomes/${id}/attachments`);

    if (response.statusCode === 200 && response.data) {
      const lo = response.data;

      // Wrap attachments inside .file for your existing gallery logic
      loAttachments.value = lo.attachments?.map(att => ({ file: att })) || [];

      // Capture author from backend
      loAuthor.value = lo.author || null;
    }
  } catch (e) {
    console.error('Failed to load LO attachments', e);
  }
};

// actions
const loadTask = async () => {
    const taskDetails = await taskStore.fetchTaskDetails(taskId.value);

    if (taskDetails) {
        task.value = {
            id: taskDetails.id,
            created: formatDisplayDate(taskDetails.created_at),
            author: taskDetails.author?.name + ' ' + taskDetails.author?.surname,
            authorEmail: taskDetails.author?.email,
            department_from: taskDetails.department_from?.name,
            department_to: taskDetails.department_to?.name,
            source: taskDetails.source?.name,
            theme: taskDetails.theme?.name,
            title: taskDetails.title,
            statusSettable: taskDetails.statusSettable,
            users_assigned_to: taskDetails.users_assigned_to?.map((userObj) => {
                return {
                    id: userObj.user.id,
                    fullname: userObj.user?.name + ' ' + userObj.user?.surname,
                    row_id: userObj.id,
                    user: userObj.user,
                    created_at: userObj.created_at,
                    delegated_at: userObj.delegated_at,
                    delegated_by: userObj.delegated_by,
                    delegated_to: userObj.delegated_to,
                };
            }),
            users_notified: taskDetails.users_notified?.map((userObj) => {
                return {
                    id: userObj.user.id,
                    fullname: userObj.user?.name + ' ' + userObj.user?.surname,
                };
            }),
            linked_to: taskDetails.linked_to?.map((item) => item.linked_to),
            linked_by: taskDetails.linked_by?.map((item) => item.linked_by),
            start_date: formatDate(taskDetails.start_date),
            due_date: formatDate(taskDetails.due_date),
            // reminder_date: formatDate(taskDetails.reminder_date),
            priority: parseInt(taskDetails.priority),
            progress: parseInt(taskDetails.progress),
            assurance_level: taskDetails.assurance_level,
            description: taskDetails.description,
            attachment: taskDetails.attachment,
            
        };
        
        if (taskDetails.learning_outcome_id) {
                await fetchLoAttachments(taskDetails.learning_outcome_id)
        }

        await loadBreadCrumbDetails(taskDetails);
    }
};

const loadBreadCrumbDetails = async (taskDetails) => {
    task.value.from_dept_breadcrumb = await setupDepartmentDetails(taskDetails.from_department_id);
    task.value.to_dept_breadcrumb = await setupDepartmentDetails(taskDetails.to_department_id, true);
    task.value.src_breadcrumb = await setupSourceDetails(taskDetails.source_id);
    task.value.theme_breadcrumb = await setupThemeDetails(taskDetails.theme_id);
};

const setupDepartmentDetails = async (deptId, managerFlag = false) => {
    const deptDetails = await departmentStore.fetchDepartmentDetails(deptId);
    // if managerFlag is true, load managers id information from the respective departments as well
    if (managerFlag) {
        managers.value = deptDetails?.managers?.map((manager) => manager.user_id);
    }
    return formatChildParentBreadCrumb(deptDetails);
};

const setupSourceDetails = async (srcId) => {
    const srcDetails = await sourceStore.fetchSourceDetails(srcId);
    return formatChildParentBreadCrumb(srcDetails);
};

const setupThemeDetails = async (themeId) => {
    const themeDetails = await themeStore.fetchThemeDetails(themeId);
    return formatChildParentBreadCrumb(themeDetails);
};

const switchStatus = (changedProgress) => {
    task.value.progress = changedProgress;
};

function goBack() {
    router.go(-1)
}

const gotoTasks = () => {
    router.push({ name: 'Tasks' });
};

const initTaskDetails = () => {
    task.value = {
        id: null,
        author: null,
        department_from: null,
        from_dept_breadcrumb: null,
        department_to: null,
        to_dept_breadcrumb: null,
        source: null,
        src_breadcrumb: null,
        theme: null,
        theme_breadcrumb: null,
        title: null,
        users_assigned_to: [],
        users_notified: [],
        linked_to: [],
        linked_by: [],
        start_date: null,
        due_date: null,
        priority: null,
        progress: null,
        // reminder_date: null,
        description: null,
        attachment: [],
    };
};

function taskDelegationHandler(data) {

    const usersDelegated =  data.users_assigned_to?.map((userObj) => {
        return {
            id: userObj.user.id,
            fullname: userObj.user?.name + ' ' + userObj.user?.surname,
        };
    })

    task.value.users_assigned_to = usersDelegated
}
</script>

<style lang="scss">

@import '../assets/task-comments.scss';

.container {
    overflow-y: auto;
    height: 85dvh;
}
</style>
